import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Storage,
  Add,
  Edit,
  Delete,
  PlayArrow,
  CheckCircle,
  Error,
  Refresh,
  PowerSettingsNew,
  Star,
  Update
} from '@mui/icons-material';
import { useAuth } from '../services/AuthContext';

const AdminDatabase = () => {
  const { isAdmin } = useAuth();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [testingId, setTestingId] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    host: '',
    username: '',
    password: '',
    database_name: '',
    port: 3306
  });

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/database-configs');
      setConfigs(response.data);
    } catch (err) {
      console.error('Error fetching database configs:', err);
      console.error('Error details:', err.response?.data || err.message);
      setError(`Failed to fetch database configurations: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'port' ? parseInt(value) || 3306 : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const url = editingConfig 
        ? `${process.env.REACT_APP_API_URL}/admin/database-configs/${editingConfig.id}`
        : `${process.env.REACT_APP_API_URL}/admin/database-configs`;
      
      const method = editingConfig ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess(editingConfig ? 'Configuration updated successfully!' : 'Configuration created successfully!');
        setOpenDialog(false);
        resetForm();
        fetchConfigs();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to save configuration');
      }
    } catch (err) {
      setError('Network error while saving configuration');
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      name: config.name,
      host: config.host,
      username: config.username,
      password: '', // Don't pre-fill password for security
      database_name: config.database_name,
      port: config.port
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this configuration?')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/database-configs/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        setSuccess('Configuration deleted successfully!');
        fetchConfigs();
      } else {
        setError('Failed to delete configuration');
      }
    } catch (err) {
      setError('Network error while deleting configuration');
    }
  };

  const handleTestConnection = async (id) => {
    setTestingId(id);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/database-configs/${id}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      const result = await response.json();
      
      if (result.success) {
        setSuccess(`Connection successful! Response time: ${result.connection_time?.toFixed(2)}ms`);
      } else {
        setError(`Connection failed: ${result.message}`);
      }
      
      fetchConfigs(); // Refresh to update connection status
    } catch (err) {
      setError('Network error while testing connection');
    } finally {
      setTestingId(null);
    }
  };

  const handleSync = async (id) => {
    setTestingId(id);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/database-configs/${id}/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      const result = await response.json();
      
      if (result.success) {
        setSuccess(`Database sync successful! ${result.message}`);
      } else {
        setError(`Sync failed: ${result.message}`);
      }
      
      fetchConfigs();
    } catch (err) {
      setError('Network error while syncing database');
    } finally {
      setTestingId(null);
    }
  };

  const handleActivate = async (id) => {
    setTestingId(id);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/database-configs/${id}/activate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      const result = await response.json();
      
      if (result.success) {
        setSuccess(`Database activated! ${result.message}`);
      } else {
        setError(`Activation failed: ${result.message}`);
      }
      
      fetchConfigs();
    } catch (err) {
      setError('Network error while activating database');
    } finally {
      setTestingId(null);
    }
  };

  const handleUpdateSchema = async (id) => {
    setTestingId(id);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/database-configs/${id}/update-schema`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      const result = await response.json();
      
      if (result.success) {
        setSuccess(`Schema update successful! ${result.message}`);
      } else {
        setError(`Schema update failed: ${result.message}`);
      }
      
      fetchConfigs();
    } catch (err) {
      setError('Network error while updating schema');
    } finally {
      setTestingId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      host: '',
      username: '',
      password: '',
      database_name: '',
      port: 3306
    });
    setEditingConfig(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  // Check if user is admin
  if (!isAdmin) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          Access denied. Only administrators can access this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Storage sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
          <Typography variant="h4" component="h1" fontWeight="bold">
            Database Configuration
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage database connections, sync database schemas, and update table fields
        </Typography>
      </Box>

      {/* Status Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Add New Button */}
      <Box sx={{ mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenDialog(true)}
          size="large"
        >
          Add New Database Configuration
        </Button>
      </Box>

      {/* Configurations Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {configs.map((config) => (
            <Grid item xs={12} md={6} lg={4} key={config.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" component="h3" sx={{ flexGrow: 1 }}>
                      {config.name}
                      {config.is_active && (
                        <Star sx={{ color: 'warning.main', ml: 1, fontSize: 20 }} />
                      )}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {config.is_active && (
                        <Chip
                          label="Active"
                          color="warning"
                          size="small"
                          icon={<Star />}
                        />
                      )}
                      <Chip
                        label={config.is_connected ? 'Connected' : 'Disconnected'}
                        color={config.is_connected ? 'success' : 'error'}
                        size="small"
                        icon={config.is_connected ? <CheckCircle /> : <Error />}
                      />
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Host:</strong> {config.host}:{config.port}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Database:</strong> {config.database_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Username:</strong> {config.username} 
                    {config.has_password ? 
                      <Chip label="Password Set" size="small" color="success" sx={{ ml: 1, fontSize: '0.7rem' }} /> :
                      <Chip label="No Password" size="small" color="warning" sx={{ ml: 1, fontSize: '0.7rem' }} />
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Created by:</strong> {config.created_by}
                  </Typography>
                  
                  {config.last_tested && (
                    <Typography variant="caption" color="text.secondary">
                      Last tested: {new Date(config.last_tested).toLocaleString()}
                    </Typography>
                  )}
                </CardContent>
                
                <Divider />
                
                <CardActions sx={{ p: 2, flexWrap: 'wrap', gap: 1 }}>
                  <Button
                    size="small"
                    startIcon={testingId === config.id ? <CircularProgress size={16} /> : <PlayArrow />}
                    onClick={() => handleTestConnection(config.id)}
                    disabled={testingId === config.id}
                  >
                    Test
                  </Button>
                  <Button
                    size="small"
                    startIcon={testingId === config.id ? <CircularProgress size={16} /> : <Refresh />}
                    onClick={() => handleSync(config.id)}
                    disabled={testingId === config.id || !config.is_connected}
                    color="secondary"
                  >
                    Sync
                  </Button>
                  <Button
                    size="small"
                    startIcon={testingId === config.id ? <CircularProgress size={16} /> : <Update />}
                    onClick={() => handleUpdateSchema(config.id)}
                    disabled={testingId === config.id || !config.is_connected}
                    color="info"
                    variant="outlined"
                  >
                    Update Schema
                  </Button>
                  {!config.is_active && (
                    <Button
                      size="small"
                      startIcon={testingId === config.id ? <CircularProgress size={16} /> : <PowerSettingsNew />}
                      onClick={() => handleActivate(config.id)}
                      disabled={testingId === config.id || !config.is_connected}
                      color="warning"
                      variant="outlined"
                    >
                      Activate
                    </Button>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(config)}
                    color="primary"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(config.id)}
                    color="error"
                  >
                    <Delete />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {configs.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Storage sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No database configurations found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add your first database configuration to get started
          </Typography>
        </Paper>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingConfig ? 'Edit Database Configuration' : 'Add New Database Configuration'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Configuration Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    helperText="Descriptive name for this database configuration"
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label="Host"
                    name="host"
                    value={formData.host}
                    onChange={handleInputChange}
                    required
                    helperText="Database server hostname or IP address"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Port"
                    name="port"
                    type="number"
                    value={formData.port}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder={editingConfig && editingConfig.has_password ? "••••••••" : ""}
                    helperText={
                      editingConfig 
                        ? (editingConfig.has_password 
                            ? "Leave blank to keep current password" 
                            : "Enter password or leave blank for no password")
                        : "Leave blank for passwordless local connections (e.g., root with no password)"
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Database Name"
                    name="database_name"
                    value={formData.database_name}
                    onChange={handleInputChange}
                    required
                    helperText="Name of the database to connect to"
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingConfig ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default AdminDatabase;